import { isAxiosError } from 'axios';

/**
 * base-api core/error_code.py: CLAIM_FIELD_TOO_LONG = CODE(2205, ...) (WEB-04).
 * Matching is on `code_str` only: base-api allocates numeric codes by hand
 * (2201 is already used twice), so 2205 alone could later mean another,
 * retryable error and must not hide "Try again".
 */
export const CLAIM_FIELD_TOO_LONG_CODE = 2205;
export const CLAIM_FIELD_TOO_LONG_CODE_STR = 'CLAIM_FIELD_TOO_LONG';

const FALLBACK_MESSAGE =
  'Reason for expense is too long. Please shorten it and submit again.';

const nonBlank = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/**
 * Returns a member-facing message when a claim create/update was rejected
 * because a free-text field is over the server's length cap, else null.
 * Two error body shapes reach this app:
 *   base-api direct (core/exception_handler.py):
 *     { error: { code: 2205, code_str: 'CLAIM_FIELD_TOO_LONG', detail?: string, data?: {...} } }
 *   through the SSO gateway relay (what claimsAxiosInstance gets):
 *     { error: { code: 'CLAIM_FIELD_TOO_LONG', message?: string, data?: {...} } }
 * A numeric `code` alone never matches (see above).
 */
const getClaimFieldTooLongMessage = (error: unknown): string | null => {
  if (!isAxiosError(error)) {
    return null;
  }
  const body = error.response?.data as
    | {
        error?: {
          code?: unknown;
          code_str?: unknown;
          detail?: unknown;
          message?: unknown;
        };
      }
    | undefined;
  const apiError = body?.error;
  if (
    apiError == null ||
    (apiError.code_str !== CLAIM_FIELD_TOO_LONG_CODE_STR &&
      apiError.code !== CLAIM_FIELD_TOO_LONG_CODE_STR)
  ) {
    return null;
  }
  if (nonBlank(apiError.detail)) return apiError.detail;
  if (nonBlank(apiError.message)) return apiError.message;
  return FALLBACK_MESSAGE;
};

export default getClaimFieldTooLongMessage;
